import axios from 'axios';
import { useSnackbar } from 'notistack';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../../types/User';

type LoginContextType = {
    isLoggedIn: boolean;
    user: User | null;
    token: string | null;
    login: (user: User, token: string) => void;
    logout: () => void;
}

const LoginContext = createContext<LoginContextType | undefined>(undefined);

export const LoginProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(() => {
        const savedUser = localStorage.getItem('user');
        return savedUser ? JSON.parse(savedUser) : null;
    });
    const [token, setToken] = useState<string | null>(() => {
        return localStorage.getItem('token');
    });
    const [logined, setLogined] = useState(false);

    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const login = useCallback((newUser: User, newToken: string) => {
        setUser(newUser);
        setToken(newToken);
        localStorage.setItem('user', JSON.stringify(newUser));
        localStorage.setItem('token', newToken);
    }, []);

    const logout = useCallback((error?: string) => {
        setUser(null);
        setToken(null);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        navigate('/');
        if (error)
            enqueueSnackbar(error, { variant: 'error' });
        else
            enqueueSnackbar('Logged out successfully', { variant: 'success' });
    }, [navigate, enqueueSnackbar]);

    useEffect(() => {
        if (!token || !user) return;

        const requestInterceptor = axios.interceptors.request.use(
            (config) => {
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        const responseInterceptor = axios.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401)
                    logout(error.response.data ?? "Session Expired");
                if (error.response?.status === 403)
                    enqueueSnackbar(error.response.data ?? "Access Denied", { variant: 'error' });
                return Promise.reject(error);
            }
        );

        setLogined(true);
        return () => {
            axios.interceptors.request.eject(requestInterceptor);
            axios.interceptors.response.eject(responseInterceptor);
            setLogined(false);
        };
    }, [logout, token, user]);

    return (
        <LoginContext.Provider
            value={{
                isLoggedIn: logined && !!user && !!token,
                user: logined ? user : null,
                token: logined ? token : null,
                login,
                logout,
            }}
        >
            {children}
        </LoginContext.Provider>
    );
};

export const useLogin = () => {
    const context = useContext(LoginContext);
    if (!context) {
        throw new Error('useLogin must be used within a LoginProvider');
    }
    return context;
};
